/**
 * The course, as a record of how it is going.
 *
 * The progress strip on the home screen is a map: where you are, what is left,
 * when it ends. It deliberately says nothing about how well any of it went, and
 * for a while nothing else did either — `attempts` has logged every answer since
 * the first migration and no screen had ever read it back per lesson.
 *
 * This is that screen, and it also happens to be where three other things
 * finally became reachable: re-reading a rule (which existed and was hidden
 * behind a chevron on a progress row), re-drilling a lesson (which did not
 * exist), and saving one for later (which did not exist either). They are on
 * one screen because they are one activity — "which rules are shaky, and what
 * do I do about it" — and splitting them across four would mean four screens
 * each answering a quarter of the question.
 *
 * WHAT IT REFUSES TO SAY
 *
 * A percentage from two answers. Below `MIN_JUDGEABLE` the row shows how many
 * answers it has instead of a number, because 2 of 2 rendered as 100% is a
 * confident lie and the learner cannot tell it from an earned one.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpen,
  Bookmark,
  BookmarkCheck,
  ChevronDown,
  Layers,
  RotateCw,
  Star,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

import type { GrammarState } from './Progress';
import {
  MIN_JUDGEABLE,
  needsWork,
  recentAccuracy,
  SHAKY,
  weakestFirstRank,
  workSignals,
} from './score';
import { StarRating } from './StarRating';
import {
  readingOrder,
  type GrammarBookmark,
  type GrammarCourse,
  type GrammarLessonScore,
  type GrammarRating,
  type GrammarScores,
} from './types';

type Filter = 'all' | 'saved' | 'shaky';

type Row = {
  n: number;
  block: number;
  title: string;
  done_date: string | null;
  score: GrammarLessonScore | null;
  bookmark: GrammarBookmark | null;
  rating: GrammarRating | null;
};

export function GrammarReview({
  appId,
  course,
  displayName,
  primaryLang,
}: {
  appId: string;
  course: GrammarCourse;
  displayName: string;
  primaryLang?: string;
}) {
  const stateQuery = useQuery<GrammarState>({
    queryKey: [appId, 'state'],
    queryFn: () => api.grammarState<GrammarState>(appId),
  });
  const scoresQuery = useQuery<GrammarScores>({
    queryKey: [appId, 'scores'],
    queryFn: () => api.grammarScores(appId),
  });
  const bookmarksQuery = useQuery({
    queryKey: [appId, 'bookmarks'],
    queryFn: () => api.grammarBookmarks(appId),
  });
  const ratingsQuery = useQuery({
    queryKey: [appId, 'ratings'],
    queryFn: () => api.grammarRatings(appId),
  });

  const [filter, setFilter] = useState<Filter>('all');
  const [weakestFirst, setWeakestFirst] = useState(false);

  const state = stateQuery.data;
  const scores = scoresQuery.data;
  const bookmarks = bookmarksQuery.data?.bookmarks ?? [];
  const ratings = ratingsQuery.data?.ratings ?? [];

  const scoreByLesson = new Map((scores?.lessons ?? []).map((s) => [s.lesson_n, s]));
  const bookmarkByLesson = new Map(bookmarks.map((b) => [b.lesson_n, b]));
  const ratingByLesson = new Map(ratings.map((r) => [r.lesson_n, r]));

  // A lesson belongs here once there is something to say about it: it was
  // finished, it has been answered at least once, or it was saved. Listing all
  // fifty would bury four real rows under forty-six empty ones.
  const rows: Row[] = (state?.outline ?? [])
    .map((lesson) => ({
      n: lesson.n,
      block: lesson.block,
      title: readingOrder(lesson.title, course, primaryLang)[0] ?? lesson.slug,
      done_date: lesson.done_date,
      score: scoreByLesson.get(lesson.n) ?? null,
      bookmark: bookmarkByLesson.get(lesson.n) ?? null,
      rating: ratingByLesson.get(lesson.n) ?? null,
    }))
    .filter(
      (row) =>
        row.done_date !== null || (row.score?.exercises_seen ?? 0) > 0 || row.bookmark !== null,
    );

  const filtered = rows.filter((row) => {
    if (filter === 'saved') return row.bookmark !== null;
    if (filter === 'shaky') return needsWork(row.score, row.rating?.current ?? null);
    return true;
  });

  const ordered = weakestFirst
    ? [...filtered].sort(
        (a, b) =>
          weakestFirstRank(a.score, a.rating?.current ?? null) -
          weakestFirstRank(b.score, b.rating?.current ?? null),
      )
    : filtered;

  // Already in the per-lesson scores, so the Mistakes affordance costs no query.
  const strugglingTotal = rows.reduce((sum, row) => sum + (row.score?.struggling ?? 0), 0);

  const loading = stateQuery.isPending || scoresQuery.isPending;
  const failed = stateQuery.isError || scoresQuery.isError;

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-6">
      <Link
        to={`/apps/${appId}`}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 self-start text-sm"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {displayName}
      </Link>

      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold">How it is going</h1>
        <p className="text-muted-foreground text-sm">
          Every rule you have studied, how it is holding up, and the ones you saved.
        </p>
      </header>

      {loading && <p className="text-muted-foreground">Loading…</p>}
      {failed && <p className="text-destructive">Could not load your record.</p>}

      {!loading && !failed && (
        <>
          <Summary rows={rows} window={scores?.recent_window ?? 10} />

          <Topics appId={appId} rows={rows} course={course} />

          <div className="flex flex-wrap items-center gap-2">
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              All {rows.length}
            </FilterChip>
            <FilterChip active={filter === 'saved'} onClick={() => setFilter('saved')}>
              Saved {bookmarks.length}
            </FilterChip>
            <FilterChip active={filter === 'shaky'} onClick={() => setFilter('shaky')}>
              Needs work{' '}
              {rows.filter((row) => needsWork(row.score, row.rating?.current ?? null)).length}
            </FilterChip>
            <button
              type="button"
              onClick={() => setWeakestFirst((v) => !v)}
              className={cn(
                'ml-auto text-xs underline-offset-4 hover:underline',
                weakestFirst ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {weakestFirst ? 'Weakest first' : 'Course order'}
            </button>
          </div>

          <MistakesLink appId={appId} count={strugglingTotal} />

          {/* The generic per-app stats page — heatmap, streak, totals — which
              works for every app and which the grammar screens had never linked
              either. Same oversight as Mistakes, found by looking for siblings
              of it rather than waiting to trip over the next one. */}
          <Link
            to={`/apps/${appId}/stats`}
            className="text-muted-foreground hover:text-foreground self-start text-xs underline-offset-4 hover:underline"
          >
            Activity, streak and totals →
          </Link>

          {ordered.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {filter === 'saved'
                ? 'Nothing saved yet. Open a lesson and tap the bookmark to keep it here.'
                : filter === 'shaky'
                  ? 'Nothing is struggling. That is the good outcome.'
                  : 'No lessons finished yet.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {ordered.map((row) => (
                <LessonRow
                  key={row.n}
                  appId={appId}
                  row={row}
                  window={scores?.recent_window ?? 10}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}

function Summary({ rows, window: recentWindow }: { rows: Row[]; window: number }) {
  const seen = rows.reduce((sum, row) => sum + (row.score?.exercises_seen ?? 0), 0);
  const total = rows.reduce((sum, row) => sum + (row.score?.exercises_total ?? 0), 0);
  const recentAttempts = rows.reduce((sum, row) => sum + (row.score?.recent_attempts ?? 0), 0);
  const recentCorrect = rows.reduce((sum, row) => sum + (row.score?.recent_correct ?? 0), 0);

  return (
    <section className="border-border grid grid-cols-3 gap-3 border-b pb-5">
      <Tile
        value={recentAttempts >= MIN_JUDGEABLE ? pct(recentCorrect / recentAttempts) : '—'}
        label={`last ${recentWindow} per lesson`}
      />
      <Tile value={`${rows.length}`} label="lessons studied" />
      <Tile
        value={`${seen}/${total}`}
        // The trap worth naming on the screen itself: the daily deck takes six
        // exercises from a new lesson and samples the rest, so a lesson can read
        // 100% with half of it never once asked.
        label="exercises ever asked"
      />
    </section>
  );
}

function Tile({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-display text-2xl font-semibold tabular-nums">{value}</span>
      <span className="text-muted-foreground text-xs leading-tight">{label}</span>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function LessonRow({
  appId,
  row,
  window: recentWindow,
}: {
  appId: string;
  row: Row;
  window: number;
}) {
  const [open, setOpen] = useState(false);
  const score = row.score;
  const recent = recentAccuracy(score);

  return (
    <li className="border-border overflow-hidden rounded-lg border">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-expanded={open}
          // The row's own text is three spans and a number, which computes to
          // an unhelpful name; say what pressing it opens.
          aria-label={`Lesson ${row.n}, ${row.title} — details`}
        >
          <span className="text-muted-foreground w-6 shrink-0 text-xs tabular-nums">{row.n}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{row.title}</span>
          <span
            className={cn(
              'shrink-0 text-sm font-semibold tabular-nums',
              recent === null
                ? 'text-muted-foreground'
                : recent >= 0.85
                  ? 'text-success'
                  : recent >= SHAKY
                    ? 'text-foreground'
                    : 'text-destructive',
            )}
          >
            {recent === null ? `${score?.attempts ?? 0} answers` : pct(recent)}
          </span>
          {/* The learner's verdict sits NEXT TO the measured one rather than
              replacing it, because the rows worth opening are the ones where
              the two disagree — and neither number can show that alone. */}
          <StarBadge stars={row.rating?.current ?? null} />
        </button>
        <BookmarkToggle appId={appId} lessonN={row.n} bookmark={row.bookmark} />
      </div>

      {open && (
        <div className="border-border bg-muted/30 flex flex-col gap-3 border-t px-3 py-3">
          <Detail score={score} window={recentWindow} />
          <RatingRow appId={appId} row={row} />
          {row.bookmark && <NoteEditor appId={appId} lessonN={row.n} bookmark={row.bookmark} />}
          <Actions appId={appId} lessonN={row.n} exercises={score?.exercises_total ?? 0} />
        </div>
      )}
    </li>
  );
}

function Detail({
  score,
  window: recentWindow,
}: {
  score: GrammarLessonScore | null;
  window: number;
}) {
  if (!score || score.attempts === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        No answers recorded yet — the rule has been read but not drilled.
      </p>
    );
  }

  const first =
    score.first_pass_attempts > 0 ? score.first_pass_correct / score.first_pass_attempts : null;

  return (
    <dl className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <Stat
        label="first time"
        value={first === null ? '—' : pct(first)}
        // The number the lifetime average is mostly measuring, given its own
        // line so the others are readable as progress away from it.
        title="Your very first answer to each exercise in this lesson"
      />
      <Stat label="lifetime" value={pct(score.correct / score.attempts)} />
      <Stat
        label={`last ${recentWindow}`}
        value={score.recent_attempts > 0 ? `${score.recent_correct}/${score.recent_attempts}` : '—'}
      />
      <Stat label="asked" value={`${score.exercises_seen}/${score.exercises_total}`} />
      {score.struggling > 0 && (
        <span className="text-destructive font-medium">{score.struggling} keeps getting you</span>
      )}
    </dl>
  );
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <span title={title}>
      {label} <span className="text-foreground font-medium tabular-nums">{value}</span>
    </span>
  );
}

function Actions({
  appId,
  lessonN,
  exercises,
}: {
  appId: string;
  lessonN: number;
  exercises: number;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-wrap gap-2">
      <Link
        to={`/apps/${appId}/lessons/${lessonN}`}
        className="border-border hover:bg-background inline-flex min-h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium"
      >
        <BookOpen className="size-3.5" aria-hidden />
        Read the rule
      </Link>
      {exercises > 0 && (
        <button
          type="button"
          onClick={() => navigate(`/apps/${appId}/practice?mode=lesson&lesson=${lessonN}`)}
          className="border-border hover:bg-background inline-flex min-h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium"
        >
          <RotateCw className="size-3.5" aria-hidden />
          Practise {exercises} again
        </button>
      )}
    </div>
  );
}

function BookmarkToggle({
  appId,
  lessonN,
  bookmark,
}: {
  appId: string;
  lessonN: number;
  bookmark: GrammarBookmark | null;
}) {
  const queryClient = useQueryClient();
  const saved = bookmark !== null;

  const toggle = useMutation({
    mutationFn: async () => {
      if (saved) await api.removeGrammarBookmark(appId, lessonN);
      else await api.saveGrammarBookmark(appId, lessonN, null);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [appId, 'bookmarks'] }),
    onError: (err: Error) => toast.error('Could not save', { description: err.message }),
  });

  return (
    <button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      className={cn(
        'shrink-0 rounded p-1.5 transition-colors',
        saved ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
      aria-label={saved ? `Remove lesson ${lessonN} from saved` : `Save lesson ${lessonN}`}
      aria-pressed={saved}
    >
      {saved ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
    </button>
  );
}

/**
 * The note on a saved lesson.
 *
 * A star says "this was hard", which three weeks later is the part you already
 * knew. "I keep putting the verb second in subordinate clauses" is the part
 * worth coming back to, and it can only be written down while it is still true.
 */
function NoteEditor({
  appId,
  lessonN,
  bookmark,
}: {
  appId: string;
  lessonN: number;
  bookmark: GrammarBookmark;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState(bookmark.note ?? '');
  const dirty = note !== (bookmark.note ?? '');

  const save = useMutation({
    mutationFn: () => api.saveGrammarBookmark(appId, lessonN, note.trim() || null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [appId, 'bookmarks'] });
      toast.success('Note saved');
    },
    onError: (err: Error) => toast.error('Could not save the note', { description: err.message }),
  });

  return (
    <div className="flex flex-col gap-2">
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={500}
        placeholder="What was hard about this one?"
        className="border-border bg-background focus-visible:ring-ring w-full rounded-md border px-2.5 py-2 text-xs focus-visible:ring-2 focus-visible:outline-none"
      />
      {dirty && (
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="bg-primary text-primary-foreground min-h-9 self-start rounded-md px-3 text-xs font-medium"
        >
          {save.isPending ? 'Saving…' : 'Save note'}
        </button>
      )}
    </div>
  );
}

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * The current rating, compact, for a collapsed row.
 *
 * Renders nothing when a lesson has never been rated — an empty five-star
 * outline in every row would read as "you scored zero" rather than "you have
 * not said", and the two are not the same claim.
 */
function StarBadge({ stars }: { stars: number | null }) {
  if (stars === null) return null;
  return (
    <span
      className="text-streak inline-flex shrink-0 items-center gap-0.5 text-xs font-medium tabular-nums"
      aria-label={`You rated this ${stars} out of 5`}
    >
      <Star className="size-3.5" fill="currentColor" strokeWidth={0} aria-hidden />
      {stars}
    </span>
  );
}

/**
 * Rate, or re-rate, from the review screen.
 *
 * Needed for two reasons beyond convenience. Lessons finished before this
 * existed have no rating and would never get one from the end-of-session prompt
 * alone, so the feature would start life mostly empty. And a rating given the
 * day you met a rule is the least informed one you will ever give it — the
 * point is to come back and say it landed after all.
 *
 * When it HAS moved, the move is shown. "2 → 4" is the improvement the accuracy
 * numbers are trying to describe, reported by the only witness who was there.
 */
function RatingRow({ appId, row }: { appId: string; row: Row }) {
  const queryClient = useQueryClient();
  const current = row.rating?.current ?? null;
  const first = row.rating?.first ?? null;
  const moved =
    first !== null && current !== null && first !== current && row.rating!.times_rated > 1;

  const rate = useMutation({
    mutationFn: (stars: number) => api.saveGrammarRating(appId, row.n, stars),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [appId, 'ratings'] }),
    onError: (err: Error) => toast.error('Could not save that', { description: err.message }),
  });

  const signals = workSignals(row.score, current);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="text-muted-foreground text-xs">How well did it land?</span>
      <StarRating
        value={current}
        onRate={(stars) => rate.mutate(stars)}
        disabled={rate.isPending}
        label={row.title}
      />
      {moved && (
        <span className="text-success text-xs font-medium tabular-nums">
          {first} → {current}
        </span>
      )}
      {/* Says WHICH signal flagged the lesson. "Needs work" on its own cannot
          distinguish a rule the numbers dislike from one you dislike, and the
          two want different responses. */}
      {signals.self && !signals.measured && (
        <span className="text-muted-foreground text-xs">your call — the answers look fine</span>
      )}
      {signals.measured && !signals.self && current !== null && (
        <span className="text-muted-foreground text-xs">the answers disagree with you</span>
      )}
    </div>
  );
}

/**
 * The topic menu — practise a whole block, not one lesson.
 *
 * A lesson is one rule; a topic is the four or five that only make sense
 * together. "Dativ" is not lesson 31, it is lessons 31 to 34, and what actually
 * goes wrong is choosing between them — which a single-lesson drill cannot ask,
 * because inside one lesson there is nothing to choose between.
 *
 * The blocks are the curriculum's own grouping, the same ones the progress strip
 * has always drawn. No second taxonomy to keep in step with the first.
 *
 * Only topics with a finished lesson appear. A drill of rules you have not been
 * taught is not revision, it is a quiz on the unseen.
 */
function Topics({ appId, rows, course }: { appId: string; rows: Row[]; course: GrammarCourse }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const blocks = new Map<number, Row[]>();
  for (const row of rows) {
    if (row.done_date === null) continue;
    blocks.set(row.block, [...(blocks.get(row.block) ?? []), row]);
  }
  if (blocks.size === 0) return null;

  const entries = [...blocks.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <section className="border-border rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-12 w-full items-center gap-2.5 px-3 text-sm font-medium"
      >
        <Layers className="text-muted-foreground size-4" aria-hidden />
        Practise a topic
        <span className="text-muted-foreground ml-auto text-xs font-normal">
          {entries.length} topic{entries.length === 1 ? '' : 's'}
        </span>
        <ChevronDown
          className={cn('text-muted-foreground size-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <ul className="border-border flex flex-col border-t">
          {entries.map(([block, lessons]) => {
            const rated = lessons.map((l) => l.rating?.current ?? null).filter((v) => v !== null);
            const attempts = lessons.reduce((n, l) => n + (l.score?.recent_attempts ?? 0), 0);
            const correct = lessons.reduce((n, l) => n + (l.score?.recent_correct ?? 0), 0);
            return (
              <li
                key={block}
                className="border-border flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{course.blockName(block)}</p>
                  <p className="text-muted-foreground text-xs">
                    {lessons.length} lesson{lessons.length === 1 ? '' : 's'} done
                    {attempts >= MIN_JUDGEABLE ? ` · ${pct(correct / attempts)}` : ''}
                    {rated.length > 0
                      ? ` · ${(rated.reduce((a, b) => a + (b ?? 0), 0) / rated.length).toFixed(1)}★`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/apps/${appId}/practice?mode=topic&block=${block}`)}
                  className="border-border hover:bg-muted/60 inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-md border px-3 text-xs font-medium"
                >
                  <RotateCw className="size-3.5" aria-hidden />
                  Practise
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * The way in to Mistakes mode.
 *
 * The mode has worked for every app since it shipped and the grammar screens
 * have never pointed at it — so a row could say "2 keeps getting you" and then
 * offer no way to drill exactly those two. Shown only when there is something
 * in it: a mode advertising an empty pool is a dead end with a badge on it.
 *
 * Distinct from "Practise N again" on a lesson row, and the difference is the
 * pool. This one crosses lessons and holds only the exercises that have beaten
 * you repeatedly, which is a smaller and more pointed deck than any one rule.
 */
function MistakesLink({ appId, count }: { appId: string; count: number }) {
  if (count === 0) return null;
  return (
    <Link
      to={`/apps/${appId}/mistakes`}
      className="border-destructive/30 bg-destructive/5 hover:bg-destructive/10 flex min-h-12 items-center gap-2.5 rounded-lg border px-3 text-sm font-medium"
    >
      <TriangleAlert className="text-destructive size-4" aria-hidden />
      Drill the ones that keep getting you
      <span className="text-muted-foreground ml-auto text-xs font-normal tabular-nums">
        {count} exercise{count === 1 ? '' : 's'}
      </span>
    </Link>
  );
}
